import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { ChevronRightIcon } from '@heroicons/react/24/solid';
import { Button } from '@leadsales/design-system/react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { VITE_ONBOARDING_LIVE_SESSION_URL } from 'src/constants/envs';
import { useBusinessHours } from 'src/hooks/whatsapp/useBusinessHours.hook';
import { useAdvisorScheduleUrl } from 'src/hooks/whatsapp/useAdvisorScheduleUrl.hook';
import { resolveEmbeddedSignupStepGuidance } from 'src/constants/embeddedSignupSteps.constants';
import {
  formatMetaReportedMessage,
  hasReportedSignupStep,
} from '../WhatsAppConnect.functions';
import type { tConnectionFailedClosedProps } from './ConnectionFailedClosed.types';
import '../WhatsAppConnect.shared.styles.scss';

const DEFAULT_NAMESPACE = 'whatsappConnect/whatsappConnect';

export default function ConnectionFailedClosed(props: tConnectionFailedClosedProps) {
  const { t } = useTranslation(props.translationNamespace ?? DEFAULT_NAMESPACE);
  const { isLiveSessionAvailable } = useBusinessHours();
  const scheduleUrl = useAdvisorScheduleUrl();

  // INT-1217: replace the generic "try again / talk to an advisor" copy with
  // guidance for the step Meta reported. Resolution order, every branch ending in
  // renderable copy:
  //   1. Meta's own error_message (consent-gated, already Spanish) — verbatim.
  //   2. The step catalog, when Meta told us where the customer stopped.
  //   3. Today's generic copy, when it told us nothing.
  // INT-914 established this shape on the sibling error screen; this generalizes
  // it from one hard-coded code to a table.
  const stepReported = hasReportedSignupStep(props.signupStep);
  const guidance = resolveEmbeddedSignupStepGuidance(props.signupStep);
  const metaMessage = formatMetaReportedMessage(props.signupErrorReport?.message);
  const metaErrorCode = props.showsMetaErrorCode
    ? props.signupErrorReport?.code
    : null;

  const title = stepReported
    ? t(guidance.titleKey, guidance.titleDefault)
    : t('failedClosed.title', 'Parece que el proceso de conexión con Meta no se completó');

  const subtitle = stepReported
    ? t(guidance.subtitleKey, guidance.subtitleDefault)
    : t('failedClosed.subtitle', 'Si cerraste la ventana por error, puedes intentar de nuevo.');

  const advisorHref = isLiveSessionAvailable ? VITE_ONBOARDING_LIVE_SESSION_URL : scheduleUrl;
  const advisorKind = isLiveSessionAvailable ? 'live' : 'schedule';
  const advisorLabel = isLiveSessionAvailable
    ? t('footer.live', 'Unirme a sesión en vivo')
    : t('footer.schedule', 'Agendar con un asesor');
  const feedbackText = isLiveSessionAvailable
    ? t(
        'failedClosed.feedbackInHours',
        'Hay un asesor en sesión en vivo ahora. Da clic en "Unirme a sesión en vivo" abajo para que te ayude a conectar.',
      )
    : t(
        'failedClosed.feedbackOutOfHours',
        'Las sesiones en vivo con un asesor están disponibles Lunes a Viernes de 9:00 a 18:00 (hora CDMX). Da clic en "Agendar con un asesor" abajo para reservar una.',
      );

  function handleAdvisorClick() {
    props.onAdvisorCtaClick?.(advisorKind);
  }

  return (
    <div className="flex flex-col flex-1">
      <div className="flex flex-col items-center justify-center gap-6 py-12 text-center flex-1">
        <InformationCircleIcon className="w-16 h-16 text-info" aria-hidden="true" />

        <div className="flex flex-col gap-2 max-w-lg">
          <h2 className="text-2xl font-semibold text-base-content">{title}</h2>
          <p className="text-base text-base-content/60">
            {metaMessage ? (
              <>
                {/* Labelled so the customer knows the sentence is Meta's, not ours. */}
                <span className="block text-sm text-base-content/50 mb-1">
                  {t('failedClosed.metaReported', 'Esto es lo que Meta nos informó:')}
                </span>
                {/* Own element so the quoted text is addressable, not a bare
                    text node sharing a parent with the label above. */}
                <span className="block">{metaMessage}</span>
              </>
            ) : (
              subtitle
            )}
            {/* Meta can send a code without a message; the code still helps support. */}
            {typeof metaErrorCode === 'number' && (
              <span className="block text-sm text-base-content/50 mt-1">
                {t('failedClosed.metaErrorCode', { code: metaErrorCode })}
              </span>
            )}
          </p>
          {/*
            INT-1217 R4 — the report button is the only thing that raises
            error_code coverage, and a customer who just failed is the most
            likely to press it on the retry. Shown whenever we still lack a
            report, INCLUDING when Meta told us nothing at all: that is the case
            where the click matters most, so this must not be derived from
            "do we have diagnostics". Suppressed once a report is in hand —
            the ask has been answered.
          */}
          {!props.signupErrorReport && (
            <p className="text-sm text-base-content/50">
              {t(
                'reportError.reminder',
                'Si vuelve a fallar, da clic en «Reportar error a Leadsales» dentro de la ventana de Meta para enviarnos el detalle.',
              )}
            </p>
          )}
        </div>

        {/*
          INT-1217 R1 — "replace the generic «agenda una sesión»". The advisor
          offer is not removed (an out-of-hours customer with an unrecognised step
          still needs it) but it loses its highlight once the screen carries
          specific guidance, so the guidance reads as the answer and the advisor
          as the fallback. With no step reported this renders exactly as before.
        */}
        <div
          className={classnames(
            'flex items-start gap-2 px-4 py-3 rounded-lg max-w-xl text-sm leading-relaxed border',
            {
              'bg-success/10 border-success/30 text-base-content':
                isLiveSessionAvailable && !stepReported,
              'bg-info/5 border-info/30 text-base-content/80':
                !isLiveSessionAvailable && !stepReported,
              'bg-base-200/40 border-base-300 text-base-content/70': stepReported,
            },
          )}
        >
          <span aria-hidden="true" className="text-base">
            {isLiveSessionAvailable ? '🟢' : '📅'}
          </span>
          <span>{feedbackText}</span>
        </div>
      </div>

      <div className="WhatsAppConnect__actions">
        <Button color="tertiary" size="md" type="button" onClick={props.onBack}>
          {t('footer.back', 'Regresar')}
        </Button>
        <div className="WhatsAppConnect__actionsRight">
          <a
            href={advisorHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleAdvisorClick}
            className="btn btn-outline btn-md"
          >
            {advisorLabel}
          </a>
          <Button color="primary" size="md" type="button" onClick={props.onRetry}>
            {t('footer.retry', 'Intentar de nuevo')}
            <ChevronRightIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
